import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SessionIdleProvider } from './context/SessionIdleContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { EnvironmentProvider, useEnvironment } from './context/EnvironmentContext';
import { LayoutVariantProvider } from './context/LayoutVariantContext';
import { Toaster } from 'sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Payments from './pages/Payments';
import Beneficiaries from './pages/Beneficiaries';
import Analytics from './pages/Analytics';
import Accounts from './pages/Accounts';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import AuditLogs from './pages/AuditLogs';
import Info from './pages/Info';
import Onboarding from './pages/Onboarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Notifications from './pages/Notifications';
import MoneyTransfer from './pages/MoneyTransfer';
import BackOfficeDashboard from './pages/backoffice/BackOfficeDashboard';
import BackOfficeMerchants from './pages/backoffice/BackOfficeMerchants';
import BackOfficeMerchantAdd from './pages/backoffice/BackOfficeMerchantAdd';
import BackOfficeMerchantPending from './pages/backoffice/BackOfficeMerchantPending';
import BackOfficeMerchantSuspended from './pages/backoffice/BackOfficeMerchantSuspended';
import BackOfficeKyc from './pages/backoffice/BackOfficeKyc';
import BackOfficeKycPending from './pages/backoffice/BackOfficeKycPending';
import BackOfficeKycApproved from './pages/backoffice/BackOfficeKycApproved';
import BackOfficeKycRejected from './pages/backoffice/BackOfficeKycRejected';
import BackOfficeNotificationsLog from './pages/backoffice/BackOfficeNotificationsLog';
import BackOfficeNotificationsBroadcast from './pages/backoffice/BackOfficeNotificationsBroadcast';
import BackOfficeNotificationsTemplates from './pages/backoffice/BackOfficeNotificationsTemplates';
import BackOfficeAnalyticsMerchant from './pages/backoffice/BackOfficeAnalyticsMerchant';
import BackOfficeAnalyticsKyc from './pages/backoffice/BackOfficeAnalyticsKyc';
import BackOfficeAnalyticsCompliance from './pages/backoffice/BackOfficeAnalyticsCompliance';
import BackOfficeConfigGeneral from './pages/backoffice/BackOfficeConfigGeneral';
import BackOfficeConfigFees from './pages/backoffice/BackOfficeConfigFees';
import BackOfficeConfigDocs from './pages/backoffice/BackOfficeConfigDocs';
import BackOfficeConfigIntegration from './pages/backoffice/BackOfficeConfigIntegration';
import BackOfficeConfigPG from './pages/backoffice/BackOfficeConfigPG';
import BackOfficeConfigPGDetail from './pages/backoffice/BackOfficeConfigPGDetail';
import BackOfficeUsersInvite from './pages/backoffice/BackOfficeUsersInvite';
import BackOfficeUsersRoles from './pages/backoffice/BackOfficeUsersRoles';
import BackOfficeUsers from './pages/backoffice/BackOfficeUsers';
import BackOfficeAuditLogs from './pages/backoffice/BackOfficeAuditLogs';
import BackOfficeInfo from './pages/backoffice/BackOfficeInfo';
// Compliance Officer pages
import BackOfficeComplianceQueue from './pages/backoffice/BackOfficeComplianceQueue';
import BackOfficeComplianceReviews from './pages/backoffice/BackOfficeComplianceReviews';
import BackOfficeReviewHistory from './pages/backoffice/BackOfficeReviewHistory';
import BackOfficeComplianceInfoRequests from './pages/backoffice/BackOfficeComplianceInfoRequests';
import BackOfficeCompliancePerformance from './pages/backoffice/BackOfficeCompliancePerformance';
// Finance / Accounts pages
import BackOfficeFinanceOverview from './pages/backoffice/BackOfficeFinanceOverview';
import BackOfficeFinanceReconciliation from './pages/backoffice/BackOfficeFinanceReconciliation';
import BackOfficeFinanceMerchants from './pages/backoffice/BackOfficeFinanceMerchants';
import BackOfficeFinanceTransactions from './pages/backoffice/BackOfficeFinanceTransactions';
import Mandatory2FAModal from './components/Mandatory2FAModal';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';

const INTERNAL_ROLES = new Set(['admin', 'compliance_officer', 'accounts', 'operations']);

function isInternalStaff(user) {
  if (!user) return false;
  if (INTERNAL_ROLES.has(user.role)) return true;
  const perms = user.permissions || [];
  return perms.some(p => INTERNAL_ROLES.has(p));
}

const AuthSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
    <div className="spinner w-8 h-8" />
  </div>
);

const canApproveKyc = (u) => u?.role === 'admin' || u?.role === 'operations' || (u?.permissions || []).includes('operations');

const ProtectedRoute = ({ children, internalOnly = false, adminOnly = false, adminOrOps = false }) => {
  const { user, loading } = useAuth();
  const { isLive } = useEnvironment();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (loading) {
      hasRedirected.current = false;
      return;
    }
    if (!user) {
      if (!hasRedirected.current) {
        hasRedirected.current = true;
        navigate('/login', { replace: true });
      }
      return;
    }
    if (adminOnly && user.role !== 'admin') {
      if (!hasRedirected.current) {
        hasRedirected.current = true;
        navigate(isInternalStaff(user) ? '/backoffice/dashboard' : '/dashboard', { replace: true });
      }
      return;
    }
    if (adminOrOps && !canApproveKyc(user)) {
      if (!hasRedirected.current) {
        hasRedirected.current = true;
        navigate(isInternalStaff(user) ? '/backoffice/dashboard' : '/dashboard', { replace: true });
      }
      return;
    }
    if (internalOnly && !isInternalStaff(user)) {
      if (!hasRedirected.current) {
        hasRedirected.current = true;
        navigate('/dashboard', { replace: true });
      }
      return;
    }
    const isInternal = isInternalStaff(user);
    const kycRequired = !isInternal && isLive && user?.kyc_status !== 'approved';
    const isAllowed = location.pathname === '/dashboard' || location.pathname === '/onboarding';
    if (kycRequired && !isAllowed && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, adminOnly, adminOrOps, internalOnly, isLive, location.pathname, navigate]);

  if (loading) return <AuthSpinner />;
  if (!user) return <AuthSpinner />;
  if (adminOnly && user.role !== 'admin') return <AuthSpinner />;
  if (adminOrOps && !canApproveKyc(user)) return <AuthSpinner />;
  if (internalOnly && !isInternalStaff(user)) return <AuthSpinner />;
  const isInternal = isInternalStaff(user);
  const kycRequired = !isInternal && isLive && user?.kyc_status !== 'approved';
  const isAllowed = location.pathname === '/dashboard' || location.pathname === '/onboarding';
  if (kycRequired && !isAllowed) return <AuthSpinner />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (loading || !user) {
      hasRedirected.current = false;
      return;
    }
    if (!hasRedirected.current) {
      hasRedirected.current = true;
      navigate(isInternalStaff(user) ? '/backoffice/dashboard' : '/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) return <AuthSpinner />;
  if (user) return <AuthSpinner />;
  return children;
};

const DefaultRedirect = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (loading) {
      hasRedirected.current = false;
      return;
    }
    if (hasRedirected.current) return;
    hasRedirected.current = true;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    navigate(isInternalStaff(user) ? '/backoffice/dashboard' : '/dashboard', { replace: true });
  }, [loading, user, navigate]);

  if (loading) return <AuthSpinner />;
  return <AuthSpinner />;
};

const DashboardOrRedirect = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (loading || !user) {
      hasRedirected.current = false;
      return;
    }
    if (isInternalStaff(user) && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate('/backoffice/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) return <AuthSpinner />;
  if (isInternalStaff(user)) return <AuthSpinner />;
  return <Dashboard />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Merchant Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardOrRedirect /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
      <Route path="/beneficiaries" element={<ProtectedRoute><Beneficiaries /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/money-transfer" element={<ProtectedRoute><MoneyTransfer /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
      <Route path="/info" element={<ProtectedRoute><Info /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />

      {/* Back Office — All internal staff */}
      <Route path="/backoffice" element={<ProtectedRoute internalOnly><Navigate to="/backoffice/dashboard" replace /></ProtectedRoute>} />
      <Route path="/backoffice/dashboard" element={<ProtectedRoute internalOnly><BackOfficeDashboard /></ProtectedRoute>} />

      {/* Operations: Merchant & User Management */}
      <Route path="/backoffice/merchants" element={<ProtectedRoute internalOnly><BackOfficeMerchants /></ProtectedRoute>} />
      <Route path="/backoffice/merchants/add" element={<ProtectedRoute adminOrOps><BackOfficeMerchantAdd /></ProtectedRoute>} />
      <Route path="/backoffice/merchants/pending" element={<ProtectedRoute internalOnly><BackOfficeMerchantPending /></ProtectedRoute>} />
      <Route path="/backoffice/merchants/suspended" element={<ProtectedRoute internalOnly><BackOfficeMerchantSuspended /></ProtectedRoute>} />
      <Route path="/backoffice/users" element={<ProtectedRoute internalOnly><BackOfficeUsers /></ProtectedRoute>} />
      <Route path="/backoffice/users/invite" element={<ProtectedRoute internalOnly><BackOfficeUsersInvite /></ProtectedRoute>} />
      <Route path="/backoffice/users/roles" element={<ProtectedRoute adminOnly><BackOfficeUsersRoles /></ProtectedRoute>} />

      {/* KYC/KYB — SU direct review */}
      <Route path="/backoffice/kyc" element={<ProtectedRoute adminOnly><BackOfficeKyc /></ProtectedRoute>} />
      <Route path="/backoffice/kyc/pending" element={<ProtectedRoute adminOrOps><BackOfficeKycPending /></ProtectedRoute>} />
      <Route path="/backoffice/kyc/approved" element={<ProtectedRoute adminOnly><BackOfficeKycApproved /></ProtectedRoute>} />
      <Route path="/backoffice/kyc/rejected" element={<ProtectedRoute adminOrOps><BackOfficeKycRejected /></ProtectedRoute>} />

      {/* Compliance Officer pages */}
      <Route path="/backoffice/compliance/my-queue" element={<ProtectedRoute internalOnly><BackOfficeComplianceQueue /></ProtectedRoute>} />
      <Route path="/backoffice/compliance/all-reviews" element={<ProtectedRoute internalOnly><BackOfficeComplianceReviews /></ProtectedRoute>} />
      <Route path="/backoffice/compliance/review-history" element={<ProtectedRoute internalOnly><BackOfficeReviewHistory /></ProtectedRoute>} />
      <Route path="/backoffice/compliance/info-requests" element={<ProtectedRoute internalOnly><BackOfficeComplianceInfoRequests /></ProtectedRoute>} />
      <Route path="/backoffice/compliance/performance" element={<ProtectedRoute internalOnly><BackOfficeCompliancePerformance /></ProtectedRoute>} />

      {/* Finance / Accounts pages */}
      <Route path="/backoffice/finance/overview" element={<ProtectedRoute internalOnly><BackOfficeFinanceOverview /></ProtectedRoute>} />
      <Route path="/backoffice/finance/reconciliation" element={<ProtectedRoute internalOnly><BackOfficeFinanceReconciliation /></ProtectedRoute>} />
      <Route path="/backoffice/finance/merchants" element={<ProtectedRoute internalOnly><BackOfficeFinanceMerchants /></ProtectedRoute>} />
      <Route path="/backoffice/finance/transactions" element={<ProtectedRoute internalOnly><BackOfficeFinanceTransactions /></ProtectedRoute>} />

      {/* Notifications & Analytics (internal staff) */}
      <Route path="/backoffice/notifications/log" element={<ProtectedRoute internalOnly><BackOfficeNotificationsLog /></ProtectedRoute>} />
      <Route path="/backoffice/notifications/broadcast" element={<ProtectedRoute adminOnly><BackOfficeNotificationsBroadcast /></ProtectedRoute>} />
      <Route path="/backoffice/notifications/templates" element={<ProtectedRoute adminOnly><BackOfficeNotificationsTemplates /></ProtectedRoute>} />
      <Route path="/backoffice/analytics/merchant-growth" element={<ProtectedRoute internalOnly><BackOfficeAnalyticsMerchant /></ProtectedRoute>} />
      <Route path="/backoffice/analytics/kyc-funnel" element={<ProtectedRoute internalOnly><BackOfficeAnalyticsKyc /></ProtectedRoute>} />
      <Route path="/backoffice/analytics/compliance" element={<ProtectedRoute internalOnly><BackOfficeAnalyticsCompliance /></ProtectedRoute>} />

      {/* System Config (SU only) */}
      <Route path="/backoffice/config/general" element={<ProtectedRoute adminOnly><BackOfficeConfigGeneral /></ProtectedRoute>} />
      <Route path="/backoffice/config/fees" element={<ProtectedRoute adminOnly><BackOfficeConfigFees /></ProtectedRoute>} />
      <Route path="/backoffice/config/docs" element={<ProtectedRoute adminOnly><BackOfficeConfigDocs /></ProtectedRoute>} />
      <Route path="/backoffice/config/integration" element={<ProtectedRoute adminOnly><BackOfficeConfigIntegration /></ProtectedRoute>} />
      <Route path="/backoffice/config/pg" element={<ProtectedRoute adminOnly><BackOfficeConfigPG /></ProtectedRoute>} />
      <Route path="/backoffice/config/pg/:code" element={<ProtectedRoute adminOnly><BackOfficeConfigPGDetail /></ProtectedRoute>} />
      <Route path="/backoffice/audit-logs" element={<ProtectedRoute internalOnly><BackOfficeAuditLogs /></ProtectedRoute>} />
      <Route path="/backoffice/info" element={<ProtectedRoute adminOnly><BackOfficeInfo /></ProtectedRoute>} />

      {/* Default Redirect */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SessionIdleProvider>
          <EnvironmentProvider>
          <NotificationsProvider>
            <LayoutVariantProvider>
              <AppRoutes />
              <ForcePasswordChangeModal />
              <Mandatory2FAModal />
              <Toaster position="top-right" richColors />
            </LayoutVariantProvider>
          </NotificationsProvider>
          </EnvironmentProvider>
        </SessionIdleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
